import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import api from "../services/api"
import { appPath } from "../config/paths"
import logoUrl from "../logo.webp"
import { sanitizeTextInput } from "../utils/sanitizeInput"

const C = {
    navy: "#002B54",
    navyLight: "#163354",
    navyMid: "#1e4070",
    blue: "#1a6fc4",
    blueLight: "#3b8de0",
    bluePale: "#e8f0fb",
    bluePale2: "#dceaf8",
    white: "#ffffff",
    offWhite: "#f5f7fa",
    gray50: "#f8fafc",
    gray100: "#eef1f5",
    gray200: "#dde2ea",
    gray300: "#c3cad5",
    gray400: "#94a0b0",
    gray500: "#64748b",
    gray700: "#334155",
    green: "#15803d",
    greenBg: "#dcfce7",
    greenBorder: "#86efac",
    red: "#dc2626",
    redBg: "#fef2f2",
    redBorder: "#fca5a5",
    orange: "#F4811F",
    orangeBg: "#fff4eb",
    purple: "#7c3aed",
    purpleBg: "#f5f3ff",
}

function PersonIcon({ size = 24, color = C.navy }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    )
}

function BuildingIcon({ size = 24, color = C.navy }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
            <path d="M9 22v-4h6v4" />
            <path d="M8 6h.01" />
            <path d="M16 6h.01" />
            <path d="M12 6h.01" />
            <path d="M12 10h.01" />
            <path d="M12 14h.01" />
            <path d="M16 10h.01" />
            <path d="M16 14h.01" />
            <path d="M8 10h.01" />
            <path d="M8 14h.01" />
        </svg>
    )
}

function BriefcaseIcon({ size = 24, color = C.navy }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
    )
}

function ShieldCheckIcon({ size = 24, color = C.green }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="m9 12 2 2 4-4" />
        </svg>
    )
}

function MailIcon({ size = 24, color = C.gray500 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
        </svg>
    )
}

export default function ProfileSettings() {
    const navigate = useNavigate()
    const [user, setUser] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [message, setMessage] = useState(null)

    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [company, setCompany] = useState("")
    const [title, setTitle] = useState("")

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await api.get("/auth/me")
                const userData = res.data
                setUser(userData)
                setFirstName(userData.firstName || "")
                setLastName(userData.lastName || "")
                setCompany(userData.company || "")
                setTitle(userData.title || "")
            } catch {
                navigate("/")
            } finally {
                setIsLoading(false)
            }
        }
        fetchUser()
    }, [navigate])

    const handleSave = async (e) => {
        e.preventDefault()
        setMessage(null)

        if (!firstName.trim() || !lastName.trim()) {
            setMessage({ type: "error", text: "First name and last name are required." })
            return
        }

        if (!company.trim()) {
            setMessage({ type: "error", text: "Company is required." })
            return
        }

        setIsSaving(true)
        try {
            const res = await api.put("/auth/profile", {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                company: company.trim(),
                title: title.trim(),
            })
            const updatedUser = res.data.user || res.data
            setUser(updatedUser)
            localStorage.setItem("user", JSON.stringify(updatedUser))
            setMessage({ type: "success", text: "Profile updated successfully." })
        } catch (err) {
            setMessage({
                type: "error",
                text: err.response?.data?.error || "Failed to update profile. Please try again.",
            })
        } finally {
            setIsSaving(false)
        }
    }

    const handleBack = () => navigate("/")

    if (isLoading) {
        return (
            <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.gray50 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
                    <div className="profile-spinner" />
                    <span style={{ fontSize: "14px", color: C.gray500 }}>Loading profile...</span>
                </div>
                <style>{`
                    .profile-spinner {
                        width: 40px; height: 40px;
                        border: 3px solid ${C.gray200};
                        border-top-color: ${C.blue};
                        border-radius: 50%;
                        animation: profile-spin 0.75s linear infinite;
                    }
                    @keyframes profile-spin { to { transform: rotate(360deg); } }
                `}</style>
            </div>
        )
    }

    if (!user) return null

    const initials = `${(firstName || user.email)?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "U"
    const displayName = firstName && lastName ? `${firstName} ${lastName}` : user.email

    return (
        <div style={{ minHeight: "100vh", background: C.gray50 }}>
            {/* Header */}
            <header style={{
                background: C.white,
                borderBottom: `1px solid ${C.gray200}`,
                padding: "0 32px",
                height: "64px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "sticky",
                top: 0,
                zIndex: 100,
            }}>
                <a href={appPath("/")} style={{ display: "flex", alignItems: "center", gap: "12px", textDecoration: "none" }}>
                    <img src={logoUrl} alt="Darwinbox" style={{ height: "28px" }} />
                    <div style={{ width: "1px", height: "24px", background: C.gray200 }} />
                    <span style={{ fontSize: "14px", fontWeight: "600", color: C.navy }}>Trust Center</span>
                </a>
                <button
                    onClick={handleBack}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "transparent",
                        border: `1px solid ${C.gray200}`,
                        borderRadius: "8px",
                        padding: "8px 16px",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: C.gray700,
                        cursor: "pointer",
                        transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => e.target.style.borderColor = C.gray300}
                    onMouseLeave={(e) => e.target.style.borderColor = C.gray200}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back to Trust Center
                </button>
            </header>

            {/* Hero Section */}
            <div style={{
                background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
                padding: "48px 32px",
                position: "relative",
                overflow: "hidden",
            }}>
                {/* Background pattern */}
                <div style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "400px",
                    height: "100%",
                    background: "radial-gradient(circle at 80% 50%, rgba(1,131,255,0.15) 0%, transparent 50%)",
                    pointerEvents: "none",
                }} />
                
                <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", alignItems: "center", gap: "24px", position: "relative" }}>
                    {/* Avatar */}
                    <div style={{
                        width: "88px",
                        height: "88px",
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.15)",
                        border: "3px solid rgba(255,255,255,0.3)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "32px",
                        fontWeight: "700",
                        color: "#fff",
                        flexShrink: 0,
                    }}>
                        {initials}
                    </div>
                    <div style={{ flex: 1 }}>
                        <h1 style={{ margin: "0 0 8px", fontSize: "28px", fontWeight: "800", color: "#fff", letterSpacing: "-0.5px" }}>
                            {displayName}
                        </h1>
                        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>
                                <MailIcon size={16} color="rgba(255,255,255,0.5)" />
                                {user.email}
                            </span>
                            {company && (
                                <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", color: "rgba(255,255,255,0.7)" }}>
                                    <BuildingIcon size={16} color="rgba(255,255,255,0.5)" />
                                    {company}
                                </span>
                            )}
                        </div>
                    </div>
                    {/* Status badges */}
                    <div style={{ display: "flex", gap: "10px", flexShrink: 0 }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "8px 14px",
                            borderRadius: "20px",
                            background: "rgba(255,255,255,0.15)",
                            border: "1px solid rgba(255,255,255,0.2)",
                        }}>
                            <div style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                background: user.role === "customer" ? "#22c55e" : user.role === "admin" ? "#f59e0b" : "#3b82f6",
                            }} />
                            <span style={{ fontSize: "12px", fontWeight: "700", color: "#fff", textTransform: "capitalize" }}>
                                {user.role || "Prospect"}
                            </span>
                        </div>
                        {user.role === "admin" ? (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "8px 14px",
                                borderRadius: "20px",
                                background: "rgba(1,131,255,0.2)",
                                border: "1px solid rgba(1,131,255,0.3)",
                            }}>
                                <ShieldCheckIcon size={14} color="#0183FF" />
                                <span style={{ fontSize: "12px", fontWeight: "700", color: "#0183FF" }}>Full Access</span>
                            </div>
                        ) : user.ndaSigned && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "8px 14px",
                                borderRadius: "20px",
                                background: "rgba(34,197,94,0.2)",
                                border: "1px solid rgba(34,197,94,0.3)",
                            }}>
                                <ShieldCheckIcon size={14} color="#22c55e" />
                                <span style={{ fontSize: "12px", fontWeight: "700", color: "#22c55e" }}>NDA Signed</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: "32px" }}>
                        {/* Left Column - Edit Form */}
                        <div style={{
                            background: C.white,
                            border: `1px solid ${C.gray200}`,
                            borderRadius: "16px",
                            overflow: "hidden",
                        }}>
                            {/* Section Header */}
                            <div style={{
                                padding: "20px 24px",
                                borderBottom: `1px solid ${C.gray100}`,
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                            }}>
                                <div style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "10px",
                                    background: C.bluePale,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}>
                                    <PersonIcon size={20} color={C.blue} />
                                </div>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: C.navy }}>
                                        Edit Profile
                                    </h2>
                                    <p style={{ margin: "2px 0 0", fontSize: "13px", color: C.gray500 }}>
                                        Update your personal information
                                    </p>
                                </div>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSave} style={{ padding: "24px" }}>
                                {/* Message */}
                                {message && (
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        padding: "14px 16px",
                                        borderRadius: "10px",
                                        background: message.type === "success" ? C.greenBg : C.redBg,
                                        border: `1px solid ${message.type === "success" ? C.greenBorder : C.redBorder}`,
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: message.type === "success" ? C.green : C.red,
                                        marginBottom: "24px",
                                    }}>
                                        <span style={{ fontSize: "18px" }}>{message.type === "success" ? "✓" : "!"}</span>
                                        {message.text}
                                    </div>
                                )}

                                {/* Name Fields */}
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                                    <div>
                                        <label style={{
                                            display: "block",
                                            fontSize: "13px",
                                            fontWeight: "600",
                                            color: C.gray700,
                                            marginBottom: "8px",
                                        }}>
                                            First Name <span style={{ color: C.red }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={firstName}
                                            onChange={(e) => setFirstName(sanitizeTextInput(e.target.value))}
                                            required
                                            placeholder="John"
                                            style={{
                                                width: "100%",
                                                boxSizing: "border-box",
                                                padding: "12px 14px",
                                                borderRadius: "10px",
                                                border: `1.5px solid ${C.gray200}`,
                                                fontSize: "14px",
                                                color: C.navy,
                                                outline: "none",
                                                transition: "border-color 0.15s, box-shadow 0.15s",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = C.blue
                                                e.target.style.boxShadow = `0 0 0 3px ${C.bluePale}`
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = C.gray200
                                                e.target.style.boxShadow = "none"
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{
                                            display: "block",
                                            fontSize: "13px",
                                            fontWeight: "600",
                                            color: C.gray700,
                                            marginBottom: "8px",
                                        }}>
                                            Last Name <span style={{ color: C.red }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={lastName}
                                            onChange={(e) => setLastName(sanitizeTextInput(e.target.value))}
                                            required
                                            placeholder="Doe"
                                            style={{
                                                width: "100%",
                                                boxSizing: "border-box",
                                                padding: "12px 14px",
                                                borderRadius: "10px",
                                                border: `1.5px solid ${C.gray200}`,
                                                fontSize: "14px",
                                                color: C.navy,
                                                outline: "none",
                                                transition: "border-color 0.15s, box-shadow 0.15s",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = C.blue
                                                e.target.style.boxShadow = `0 0 0 3px ${C.bluePale}`
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = C.gray200
                                                e.target.style.boxShadow = "none"
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Company Field */}
                                <div style={{ marginBottom: "20px" }}>
                                    <label style={{
                                        display: "block",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: C.gray700,
                                        marginBottom: "8px",
                                    }}>
                                        Company <span style={{ color: C.red }}>*</span>
                                    </label>
                                    <div style={{ position: "relative" }}>
                                        <div style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                                            <BuildingIcon size={18} color={C.gray400} />
                                        </div>
                                        <input
                                            type="text"
                                            value={company}
                                            onChange={(e) => setCompany(sanitizeTextInput(e.target.value))}
                                            required
                                            placeholder="Your organization name"
                                            style={{
                                                width: "100%",
                                                boxSizing: "border-box",
                                                padding: "12px 14px 12px 42px",
                                                borderRadius: "10px",
                                                border: `1.5px solid ${C.gray200}`,
                                                fontSize: "14px",
                                                color: C.navy,
                                                outline: "none",
                                                transition: "border-color 0.15s, box-shadow 0.15s",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = C.blue
                                                e.target.style.boxShadow = `0 0 0 3px ${C.bluePale}`
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = C.gray200
                                                e.target.style.boxShadow = "none"
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Title Field */}
                                <div style={{ marginBottom: "28px" }}>
                                    <label style={{
                                        display: "block",
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: C.gray700,
                                        marginBottom: "8px",
                                    }}>
                                        Job Title
                                    </label>
                                    <div style={{ position: "relative" }}>
                                        <div style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                                            <BriefcaseIcon size={18} color={C.gray400} />
                                        </div>
                                        <input
                                            type="text"
                                            value={title}
                                            onChange={(e) => setTitle(sanitizeTextInput(e.target.value))}
                                            placeholder="e.g. Security Analyst, Procurement Manager"
                                            style={{
                                                width: "100%",
                                                boxSizing: "border-box",
                                                padding: "12px 14px 12px 42px",
                                                borderRadius: "10px",
                                                border: `1.5px solid ${C.gray200}`,
                                                fontSize: "14px",
                                                color: C.navy,
                                                outline: "none",
                                                transition: "border-color 0.15s, box-shadow 0.15s",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = C.blue
                                                e.target.style.boxShadow = `0 0 0 3px ${C.bluePale}`
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = C.gray200
                                                e.target.style.boxShadow = "none"
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    style={{
                                        width: "100%",
                                        padding: "14px",
                                        borderRadius: "10px",
                                        border: "none",
                                        background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`,
                                        color: "#fff",
                                        fontSize: "14px",
                                        fontWeight: "700",
                                        cursor: isSaving ? "wait" : "pointer",
                                        opacity: isSaving ? 0.7 : 1,
                                        transition: "all 0.2s",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                    }}
                                >
                                    {isSaving ? (
                                        <>
                                            <div style={{
                                                width: "16px",
                                                height: "16px",
                                                border: "2px solid rgba(255,255,255,0.3)",
                                                borderTopColor: "#fff",
                                                borderRadius: "50%",
                                                animation: "profile-spin 0.75s linear infinite",
                                            }} />
                                            Saving...
                                        </>
                                    ) : "Save Changes"}
                                </button>
                            </form>
                        </div>

                        {/* Right Column - Info */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                            {/* Quick Info Card */}
                            <div style={{
                                background: C.white,
                                border: `1px solid ${C.gray200}`,
                                borderRadius: "16px",
                                padding: "20px",
                            }}>
                                <h3 style={{ margin: "0 0 16px", fontSize: "14px", fontWeight: "700", color: C.navy, display: "flex", alignItems: "center", gap: "8px" }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M12 16v-4" />
                                        <path d="M12 8h.01" />
                                    </svg>
                                    Important Note
                                </h3>
                                <p style={{ margin: 0, fontSize: "13px", color: C.gray500, lineHeight: "1.7" }}>
                                    Your <strong style={{ color: C.navy }}>full legal name</strong> is used for NDA agreements and document access requests. Please ensure it matches your official identification.
                                </p>
                            </div>

                            {/* Account Summary */}
                            <div style={{
                                background: C.white,
                                border: `1px solid ${C.gray200}`,
                                borderRadius: "16px",
                                padding: "20px",
                            }}>
                                <h3 style={{ margin: "0 0 16px", fontSize: "14px", fontWeight: "700", color: C.navy }}>
                                    Account Summary
                                </h3>
                                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontSize: "13px", color: C.gray500 }}>Account Type</span>
                                        <span style={{
                                            fontSize: "12px",
                                            fontWeight: "700",
                                            padding: "4px 10px",
                                            borderRadius: "20px",
                                            textTransform: "capitalize",
                                            background: user.role === "customer" ? C.greenBg : user.role === "admin" ? C.orangeBg : C.bluePale,
                                            color: user.role === "customer" ? C.green : user.role === "admin" ? C.orange : C.blue,
                                        }}>
                                            {user.role || "Prospect"}
                                        </span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontSize: "13px", color: C.gray500 }}>NDA Status</span>
                                        <span style={{
                                            fontSize: "12px",
                                            fontWeight: "700",
                                            padding: "4px 10px",
                                            borderRadius: "20px",
                                            background: user.role === "admin" ? C.bluePale : user.ndaSigned ? C.greenBg : C.gray100,
                                            color: user.role === "admin" ? C.blue : user.ndaSigned ? C.green : C.gray500,
                                        }}>
                                            {user.role === "admin" ? "N/A" : user.ndaSigned ? "Signed" : "Not Signed"}
                                        </span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontSize: "13px", color: C.gray500 }}>Documents Access</span>
                                        <span style={{ fontSize: "13px", fontWeight: "600", color: C.navy }}>
                                            {user.role === "admin"
                                                ? `All (${user.totalDocuments || 0})`
                                                : `${user.accessibleDocumentCount ?? user.accessibleDocs?.length ?? 0} / ${user.totalDocuments || 0}`}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
            </div>

            <style>{`
                @keyframes profile-spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    )
}
